/*!

=========================================================
* Argon Design System React - v1.1.2
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-design-system-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-design-system-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";
// reactstrap components
import {
  Button,
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Form,
  FormGroup,
  InputGroup,
  InputGroupText,
  Input,
  Modal
} from "reactstrap";

// core components

import { getStats } from "../../components/helpers/StatsHelpers";
import { RiUser3Line } from "react-icons/ri";
import { requestPasswordResetForEmail, requestPasswordResetForUserId } from "../../components/helpers/UserHelpers";
import { useParams } from "react-router-dom";
import withNavigateHook from "../../hooks/withNavigationHook";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";

class PasswordReset extends React.Component {
  constructor(props) {
    super(props);
    getStats.call(this);

    this.state = {
      email: "",
      emailError: "",
      emailSubmitted: false,
      formModalResetIssue: false, // For error handling
    };
  }

  componentDidMount() {
    // If a customer is logged in, request a password reset automatically.
    if (sessionStorage.getItem("customer")) {
      requestPasswordResetForUserId(JSON.parse(sessionStorage.getItem("customer")).user.id);
    }
  }

  onChangeEmail = (e) => {
    this.setState({
      email: e.target.value,
      emailError: "",
    });
  };

  resetButtonAvailable = () => {
    return this.state.email;
  };

  validateAndSubmitForm = (e) => {
    e.preventDefault();
    this.setState({ formSubmitted: true });
    let formHasError = false;

    // Check if email is provided
    if (!this.state.email || this.state.email.trim().length === 0) {
      this.setState({ emailError: "* You must provide an email address" });
      formHasError = true;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.state.email)) {
      this.setState({ emailError: "* Please enter a valid email address" });
      formHasError = true;
    }

    if (!formHasError) {
      requestPasswordResetForEmail(this.state.email).then((result) => {
        if (result) {
          // Replace the form with a confirmation message
          this.setState({ emailSubmitted: true });
        } else {
          // Optionally, you can display an error modal or message here
          this.toggleModal("formModalResetIssue");
        }
      });
    }
    this.setState({ formSubmitted: false });
  };

  toggleModal = (state) => {
    this.setState({
      [state]: !this.state[state],
    });
  };

  toggleModalAndMoveToHome = (state) => {
    this.toggleModal(state);
    this.props.navigation("/");
  };

  render() {
    return (
        <>
          <SeoNoIndex uri="/password-reset" description="Reset your password for The Worship Bible" />
          <div className="position-relative">
            {/* Hero Section */}
            <section className="section section-lg section-shaped pb-250">
              <div className="shape shape-style-1 shape-default">
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
              </div>
              <Container className="py-lg-md d-flex">
                <div className="col px-0">
                  <Row>
                    <Col lg="8">
                      <h1 className="display-3 text-white">
                        Building your song-flows{" "}
                        <span>password reset</span>
                      </h1>
                    </Col>
                  </Row>
                </div>
              </Container>
              {/* SVG separator */}
              <div className="separator separator-bottom separator-skew">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none"
                    version="1.1"
                    viewBox="0 0 2560 100"
                    x="0"
                    y="0"
                >
                  <polygon className="fill-white" points="2560 0 2560 100 0 100" />
                </svg>
              </div>
            </section>
          </div>
          <section className="section section-lg pt-lg-0 mt--200">
            <Container>
              <Row className="justify-content-center">
                <Col lg="12">
                  <Row className="row-grid">
                    <Col lg="12">
                      <Card className="card-lift--hover shadow border-0">
                        <CardBody className="py-5">
                          <Row>
                            <Col>
                              <h2 style={{ fontSize: "1rem" }} className="text-primary text-uppercase">
                                <RiUser3Line color="#5e72e4" size="36" />&nbsp;Request password reset email
                              </h2>
                            </Col>
                          </Row>
                          {/* If a customer is logged in OR the email was just submitted, show the confirmation message */}
                          {(sessionStorage.getItem("customer") || this.state.emailSubmitted) ? (
                              <>
                                <p className="description mt-3">
                                  If the email address provided is registered on The Worship Bible an email has been sent to that address.
                                </p>
                                <p className="description mt-3">
                                  Click on the link in the email to reset your password.
                                </p>
                              </>
                          ) : (
                              <>
                                <p className="description mt-3">
                                  To reset your password please provide your email address.
                                </p>
                                <Form
                                    role="form"
                                    onSubmit={this.validateAndSubmitForm}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        this.validateAndSubmitForm(e);
                                      }
                                    }}
                                >
                                  <FormGroup>
                                    <Row lg="12">
                                      <Col lg="12">
                                        <InputGroup className="input-group-alternative mb-3">
                                          <InputGroupText>
                                            <i className="ni ni-email-83" />
                                          </InputGroupText>
                                          <Input
                                              name="email"
                                              placeholder="Email"
                                              type="email"
                                              onChange={this.onChangeEmail}
                                              value={this.state.email}
                                          />
                                        </InputGroup>
                                        {this.state.emailError && (
                                            <small className="text-danger">{this.state.emailError}</small>
                                        )}
                                      </Col>
                                    </Row>
                                  </FormGroup>
                                  <div className="text-center">
                                    <Button
                                        id="loginButton"
                                        className="mt-4"
                                        color="primary"
                                        type="submit"
                                        disabled={!this.resetButtonAvailable()}
                                    >
                                      Request password reset email
                                    </Button>
                                  </div>
                                </Form>
                              </>
                          )}
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Container>
          </section>
        </>
    );
  }
}

export default withNavigateHook((props) => (
    <PasswordReset {...props} params={useParams()} />
));
